import React from 'react';
import styled from 'styled-components';
import Spinner from '../spinner/Spinner';

export enum ButtonColor {
  green = 'GREEN',
  blue = 'BLUE',
  red = 'RED',
  pink = 'PINK',
  gray = 'GRAY',
  gray10 = 'GRAY10',
  white = 'WHITE',
}

const getButtonColor = (color: ButtonColor) => {
  switch (color) {
    case ButtonColor.red:
      return 'var(--color-red)';
    case ButtonColor.green:
      return 'var(--color-green)';
    case ButtonColor.blue:
      return 'var(--color-blue)';
    case ButtonColor.pink:
      return 'var(--color-pink)';
    case ButtonColor.gray:
      return 'var(--color-gray-800)';
    case ButtonColor.gray10:
      return 'var(--color-gray-800-10)';
    case ButtonColor.white:
      return 'var(--color-white)';
  }
};

const getButtonHoverColor = (color: ButtonColor) => {
  switch (color) {
    case ButtonColor.red:
      return 'var(--color-button-red-hover)';
    case ButtonColor.green:
      return 'var(--color-button-green-hover)';
    case ButtonColor.blue:
      return 'var(--color-button-blue-hover)';
    case ButtonColor.pink:
      return 'var(--color-button-pink-hover)';
    case ButtonColor.gray:
      return 'var(--color-gray-950)';
    case ButtonColor.gray10:
      return 'var(--color-gray-800-20)';
    case ButtonColor.white:
      return 'var(--color-white)';
  }
};

type ButtonWrapperProps = {
  large: boolean;
  color: ButtonColor;
  borderRadius?: String;
  padding?: String;
  gap?: String;
  textColor?: string;
  textColorHover?: string;
  boxShadow?: string;
};

const ButtonWrapper = styled.button<ButtonWrapperProps>`
  font-family: 'Poppins', sans-serif;
  font-weight: 500;
  border: none;
  padding: 1rem ${(p) => (p.large ? '5rem' : '2rem')};
  ${({ padding }) => `padding: ${padding || ''}`};
  border-radius: ${({ borderRadius }) => (borderRadius ? `${borderRadius}` : '1.5rem')};
  color: ${(p) => (p?.textColor ? p.textColor : 'var(--color-white)')};
  font-size: 1.4rem;
  display: flex;
  gap: ${({ gap }) => (gap ? `${gap}` : '1.5rem')};
  justify-content: center;
  align-items: center;
  background-color: ${(p) => getButtonColor(p.color)};
  cursor: pointer;
  transition: color 0.3s, background-color 0.3s;
  box-shadow: ${(p) => (p.boxShadow ? p.boxShadow : 'none')};

  svg {
    fill: ${(p) => (p.textColor ? p.textColor : 'var(--color-white)')};
    width: 1.7rem;
    path {
      fill: ${(p) => (p.textColor ? p.textColor : 'var(--color-white)')};
    }
  }

  &:focus,
  &:hover {
    outline: none;
    background-color: ${(p) => getButtonHoverColor(p.color)};
    color: ${(p) => (p?.textColorHover ? p.textColorHover : '')};
    svg {
      fill: ${(p) => (p.textColorHover ? p.textColorHover : '')};
      path {
        fill: ${(p) => (p.textColorHover ? p.textColorHover : '')};
      }
    }
  }

  &:disabled {
    cursor: not-allowed;
    svg {
      fill: var(--color-button-disabled-text);
    }
    background-color: var(--color-button-disabled-background);
    color: var(--color-button-disabled-text);
  }
`;

type Props = {
  color: ButtonColor;
  label: string;
  onClick: () => void;
  large?: boolean;
  disabled?: boolean;
  iconSVG?: JSX.Element;
  loading?: boolean;
  borderRadius?: String;
  padding?: String;
  gap?: String;
  textColor?: string;
  textColorHover?: string;
  boxShadow?: string;
};

const Button = (props: Props) => {
  return (
    <ButtonWrapper
      large={props.large}
      color={props.color}
      onClick={props.onClick}
      disabled={props.disabled}
      borderRadius={props.borderRadius}
      padding={props.padding}
      gap={props.gap}
      textColor={props.textColor}
      textColorHover={props.textColorHover}
      boxShadow={props.boxShadow}
    >
      {props.loading ? (
        <Spinner />
      ) : (
        <>
          {props.iconSVG && props.iconSVG}
          {props.label}
        </>
      )}
    </ButtonWrapper>
  );
};

export default Button;
